import React from "react";
import { View, StyleSheet, SafeAreaView, Platform } from "react-native";
import { useSelector } from "react-redux";
import { Text, Spacer } from "../../components";
import { Stack } from "../../components/layout/stack";
import { useNavigation, CommonActions } from "@react-navigation/native";
import { useTheme, Headline } from "react-native-paper";
import { ThemeType } from "../../theme/theme";
import { translate } from "../../i18n/translate";
import { useDispatch } from "react-redux";
import { HomeParamsList, HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { InfoBoxIconType, InfoBoxType } from "../../enums";
import { navigateToHomePage } from "../../utils/navigateToHomePage";
import { RootState } from "../../redux/reducers";
import { formatPhoneString } from "../../utils/phoneNumber";
import { boolToHuman } from "../../utils";
import { domusoResponsiveStyleHelper } from "../new/utils/domuso-responsive";
import { Grid, Container, Row as RowGrid, Column } from "ui-grid";
import { BottomNav } from "../../components/layout/bottom-nav";
import { Footer } from "../../components/new/footer/footer";
import { OnScreenAlert, Button } from "ui-components";
import { ScrollView } from "react-native-gesture-handler";
import automationTestingLocators from "../../screens/new/utils/automation-testing-locators.json";

export const ServiceRequestSummary = ({ route }: HomeScreenProps) => {
  const navigation = useNavigation();
  const { params = {} as HomeParamsList } = route;
  let {
    isYardi,
    propName,
    propertyUnit,
    phone,
    hasPermission,
    hasPets,
    isHouseholdSick,
    category,
    subcategory,
    isSuccessful,
  } = params;
  const theme = useTheme() as ThemeType;
  const styles = getStyles(theme);
  const logger = useSelector((state: RootState) => state.logger);
  const dispatch = useDispatch();

  const navigateToHome = () => {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: "Home" }],
      })
    );
    navigateToHomePage({ navigation, params });
  };

  const navigateToMakeAnotherRequest = () => {
    return navigation.navigate("SubmitServiceRequest", { ...params });
  };
  const infoBoxType = isSuccessful ? InfoBoxType.GREEN : InfoBoxType.RED;
  const headlineIconType = isSuccessful ? InfoBoxIconType.GREEN_CHECK : InfoBoxIconType.RED_X;
  const headlineTx = isSuccessful ? "serviceStack.successHeadline" : "serviceStack.failureHeadline";
  const messageTx = isSuccessful ? "serviceStack.successMessage" : "serviceStack.failureMessage";
  const unitName = propertyUnit ? `#${propertyUnit}` : "Unassigned";
  const phoneNumber = formatPhoneString(phone);
  const permission = boolToHuman(hasPermission);
  const isSick = boolToHuman(isHouseholdSick);
  const pets = boolToHuman(hasPets);

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}>
        <Grid>
          <Container>
            <View style={styles.screen}>
              <RowGrid>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column
                  span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                  style={[styles.inputRow, styles.header]}>
                  <View style={[styles.inputRowInner]}>
                    <ScrollView>
                      <OnScreenAlert
                        alertType={isSuccessful ? "success" : "alert"}
                        title={translate(headlineTx) as string}
                        body={translate(messageTx) as string}
                        bodyStyle={{ fontSize: 13 }}
                        titleStye={{ fontFamily: "Lato_700Bold" }}
                      />
                    </ScrollView>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </RowGrid>
              <RowGrid>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                  <View style={[styles.inputRowInner, { marginTop: 16 }]}>
                    <Headline style={styles.paymentSummary}>
                      {translate("serviceStack.requestSummary")}
                    </Headline>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </RowGrid>
              <RowGrid>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                  <View style={[styles.inputRowInner, { marginTop: 8 }]}>
                    <Stack space="smaller" useThemeBackgroundColor={false}>
                      <Row styles={styles} title="Property" info={propName} />
                      <Row styles={styles} title="Unit" info={unitName} />
                      <Row styles={styles} title="Phone Number" info={phoneNumber} />
                      <Row styles={styles} title="Permission to Enter Unit" info={permission} />
                      <Row styles={styles} title="Has Sickness Symptoms" info={isSick} />
                      {isYardi && <Row styles={styles} title="Has Pets" info={pets} />}
                      <Row styles={styles} title="Service Request Category" info={category} />
                      {isYardi && (
                        <Row
                          styles={styles}
                          title="Service Request Subcategory"
                          info={subcategory}
                        />
                      )}
                      <Spacer />
                    </Stack>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </RowGrid>
            </View>
          </Container>
        </Grid>
      </ScrollView>
      <View style={styles.bottomNav} />

      <BottomNav backgroundColor={theme.colors.white}>
        <View style={{ marginTop: 15 }} />
        <RowGrid>
          <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
          <Column span={{ xs: 12, sm: 12, md: 9, lg: 7 }}>
            <Stack space="small">
              <Button
                text={translate("serviceStack.requestAnotherService") as string}
                mode="contained"
                onPress={navigateToMakeAnotherRequest}
                textStyle={{ fontFamily: "Lato_700Bold" }}
                {...automationTestingLocators.residentServiceRequestSummary.requestAnotherService}
              />
              <Button
                text={translate("homeStack.returnToHome") as string}
                mode="contained"
                onPress={navigateToHome}
                textStyle={{ fontFamily: "Lato_700Bold" }}
                {...automationTestingLocators.residentServiceRequestSummary.returnHome}
              />
            </Stack>
          </Column>
          <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
        </RowGrid>
      </BottomNav>
      <Footer />
    </SafeAreaView>
  );
};

interface IRowProps extends Record<string, any> {
  styles: any;
  title: string;
  info: any;
  isBold?: boolean;
}

const Row = ({ styles, title, info, isBold = false }: IRowProps) => {
  const content = typeof info === "string" ? <Text style={styles.contentCell}>{info}</Text> : info;
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{title}</Text>
      {content}
    </View>
  );
};

const getStyles = (theme: ThemeType) => {
  const { colors, spacing, fontSize } = theme;
  return StyleSheet.create({
    container: {
      paddingTop: spacing.small,
      flex: 1,
      ...domusoResponsiveStyleHelper({
        webPhone: {
          backgroundColor: theme.colors.white,
        },
        webTablet: {
          backgroundColor: "#FAFAFA",
        },
        webDesktop: {
          backgroundColor: "#FAFAFA",
        },
        iosPhone: {
          backgroundColor: theme.colors.white,
        },
        iosTablet: {
          backgroundColor: "#FAFAFA",
        },
        androidPhone: {
          backgroundColor: theme.colors.white,
        },
        androidTablet: {
          backgroundColor: "#FAFAFA",
        },
      }),
    },
    mainStack: {
      // display: "flex",
      // flex: 1,
    },
    screen: {
      // justifyContent: "space-between",
      // paddingHorizontal: spacing.medium,
      // display: "flex",
      // flex: 1,
    },
    iconSize: {
      height: theme.iconSize.small,
      width: theme.iconSize.small,
      resizeMode: "contain",
    },
    paymentSummary: {
      fontSize: fontSize.larger,
      fontWeight: "600",
    },
    rowLabel: {
      fontWeight: "600",
    },
    checkboxLabel: {
      maxWidth: 300,
      textAlign: "left",
      fontSize: fontSize.medium,
    },
    row: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
    },
    contentCell: {
      maxWidth: 170,
      textAlign: "right",
    },
    scrollView: { flexGrow: 0 },
    scrollableArea: {
      ...domusoResponsiveStyleHelper({
        webPhone: { backgroundColor: theme.colors.white, paddingBottom: 10 },
        webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        iosPhone: { backgroundColor: theme.colors.white, paddingBottom: 0, marginTop: 20 },
        iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 30 },
        androidPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
        androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      }),
    },
    inputRow: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        webDesktop: {
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        iosPhone: {},
        iosTablet: {
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
          paddingLeft: 40,
          paddingRight: 40,
        },
        androidPhone: {},
        androidTablet: {
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
          paddingLeft: 40,
          paddingRight: 40,
        },
      }),
    },
    inputRowInner: {
      justifyContent: "center",
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        webDesktop: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
      }),
    },
    header: {
      ...domusoResponsiveStyleHelper({
        webPhone: { paddingVertical: 3 },
        webTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        webDesktop: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        iosPhone: { paddingVertical: 3 },
        iosTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        androidPhone: { paddingVertical: 3 },
        androidTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
      }),
    },
    bottomNav: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {},
        webDesktop: {},
        iosPhone: { flex: 1 },
        iosTablet: {},
        androidPhone: { flex: 1 },
        androidTablet: {},
      }),
    },
  });
};